<script lang="ts" setup>
import { computed, ref, type VNode } from 'vue';
import type { Placement } from '@floating-ui/vue';
import { IconChevronDown, IconCheck } from '@/icons';
import { ObHostedDropdown } from '../hosted-dropdown';
type Option = {
  label: string;
  value: string;
};
const {
  options = [],
  placeholder = 'Select',
  chevron = true,
  variant = 'primary',
  displayLabel,
  showSelectedIcon = true,
  dropdownWidth = 200,
  dropdownPlacement,
  maxWidth,
  selectedIconPosition = 'end',
} = defineProps<{
  modelValue?: string | null;
  options?: Option[];
  placeholder?: string;
  chevron?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary';
  displayLabel?: string;
  showSelectedIcon?: boolean;
  dropdownWidth?: number | string;
  dropdownPlacement?: Placement;
  maxWidth?: number | string;
  selectedIconPosition?: 'start' | 'end';
}>();
defineSlots<{
  icon?: () => VNode;
  dropdownTop?: () => VNode;
  dropdownBottom?: () => VNode;
  dropdownContent?: (props: { close: () => void }) => VNode;
}>();
const modelValue = defineModel<string | null>({ default: null });
const open = ref(false);
const selectedLabel = computed(() => {
  if (displayLabel) {
    return displayLabel;
  }
  const found = options.find((o) => o.value === modelValue.value);
  return found?.label ?? placeholder;
});
const resolvedDropdownWidth = computed(() =>
  typeof dropdownWidth === 'number' ? `${dropdownWidth}px` : dropdownWidth,
);
const resolvedMaxWidth = computed(() =>
  typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
);
function onHostClick() {
  open.value = !open.value;
}
function selectOption(option: Option) {
  modelValue.value = option.value;
  open.value = false;
}

function closeDropdown() {
  open.value = false;
}
</script>

<template>
  <ObHostedDropdown
    v-model:active="open"
    tabindex="-1"
    :placement="dropdownPlacement"
    shift
    flip
    :padding="5"
    @click-outside="open = false"
    @keydown.esc.prevent="open = false"
  >
    <template #host="{ hostProps }">
      <button
        v-bind="hostProps"
        type="button"
        :style="{ maxWidth: resolvedMaxWidth }"
        :class="[
          $style.root,
          {
            [$style.active]: open,
            [$style.secondary]: variant === 'secondary',
            [$style.tertiary]: variant === 'tertiary',
          },
        ]"
        @click="onHostClick"
      >
        <span v-if="$slots.icon" :class="$style.icon">
          <slot name="icon" />
        </span>
        <span :class="$style.label">{{ selectedLabel }}</span>
        <IconChevronDown v-if="chevron" :class="$style.chevron" aria-hidden="true" />
      </button>
    </template>
    <div :class="$style.dropdown" :style="{ width: resolvedDropdownWidth }">
      <slot v-if="$slots.dropdownContent" name="dropdownContent" :close="closeDropdown" />
      <template v-else>
        <div v-if="$slots.dropdownTop" :class="$style.dropdownTop">
          <slot name="dropdownTop" />
        </div>
        <div :class="$style.list">
          <button
            v-for="option in options"
            :key="option.value"
            type="button"
            :class="[$style.item, { [$style.itemSelected]: modelValue === option.value }]"
            @click="selectOption(option)"
          >
            <span
              v-if="
                showSelectedIcon &&
                selectedIconPosition === 'start' &&
                modelValue === option.value
              "
              :class="$style.itemCheck"
            >
              <IconCheck :size="16" />
            </span>
            <span :class="$style.itemLabel">{{ option.label }}</span>
            <span
              v-if="
                showSelectedIcon &&
                selectedIconPosition === 'end' &&
                modelValue === option.value
              "
              :class="$style.itemCheck"
            >
              <IconCheck :size="16" />
            </span>
          </button>
        </div>
        <div v-if="$slots.dropdownBottom" :class="$style.dropdownBottom">
          <slot name="dropdownBottom" />
        </div>
      </template>
    </div>
  </ObHostedDropdown>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  box-sizing: border-box;
  overflow: hidden;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:active,
  &.active {
    background: rgba(0, 0, 0, 0.08);

    .icon,
    .label,
    .chevron {
      color: var(--ui2-background-accent-primary, #226b6c);
    }
  }
}

.secondary {
  .icon,
  .label,
  .chevron {
    color: var(--ui2-content-secondary, #655e60);
  }
}

.tertiary {
  .icon,
  .label,
  .chevron {
    color: var(--ui2-content-primary, #010000);
  }
}

.icon {
  display: flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  overflow: hidden;
}

.label {
  @include typography.style('data-s-semibold');
  flex: 1 1 auto;
  min-width: 0;
  color: var(--ui2-content-primary, #010000);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chevron {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  color: var(--ui2-content-primary, #010000);
}

.dropdown {
  padding: 6px;
  width: 200px;
}

.dropdownTop {
  margin-bottom: 6px;
}

.dropdownBottom {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--ui2-stroke-primary, #d5d6d9);
}

.list {
  display: flex;
  flex-direction: column;
}

.item {
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;

  &:hover {
    background: var(--ui2-background-shade-5, rgba(35, 57, 93, 0.05));
  }
}

.itemLabel {
  flex: 1;
  @include typography.style('data-xs-regular');
  color: var(--ui2-content-primary, #010000);
}

.itemCheck {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--ui2-content-primary, #010000);
}

:global(.ui-select-compact-switch-row) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  padding: 8px;
  border-radius: 6px;
  box-sizing: border-box;
}
</style>
